import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import type { IconButtonProps } from '@mui/material/IconButton';
import React, { useCallback, useState } from 'react';

import type { SplitItemProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';

import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import { QDropdownMenu, QMenu } from 'components/QDropdowns';

import * as optionHooks from './options';
import { renderTxnMenuOptions, type TxnMenuOption } from './renderTxnMenuOptions';
import useTxnActionProps from './useTxnActionProps';

export enum TxnMenuOptionEnum {
  EDIT = 'EDIT',
  RULE = 'RULE',
  INVOICE = 'INVOICE',
  LINK_TO_TRANSACTION = 'LINK_TO_TRANSACTION',
  PAYMENT = 'PAYMENT',
  BILL_PRESENTMENT = 'BILL_PRESENTMENT',
  EXCLUDE_REPORTS = 'EXCLUDE_REPORTS',
  EXCLUDE_SPENDING_PLAN = 'EXCLUDE_SPENDING_PLAN',
  RECURRING = 'RECURRING',
  GOAL = 'GOAL',
  VIEW_SERIES = 'VIEW_SERIES',
  DELETE = 'DELETE',
}

interface TxnActionsMenuProps {
  anchorEl: HTMLElement | null;
  context: string;
  onMenuClose?: () => void;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  txn: TransactionRecord;
  splitItem?: SplitItemProps | null;
  txnActionCallback?: (resoures: any[]) => void;
  className?: string;
  iconClassName?: string;
  showOptionsOrder?: TxnMenuOptionEnum[];
  width?: number | string;
  isInvoiceDash?: boolean;
  splitId?: string; // used for editing exclusions for a specific split line
  variant?: IconButtonProps['variant'] | 'empty';
  size?: IconButtonProps['size'];
  rootModalDrawer?: RootModalDrawerOptions;
}

type TxnActionsMenuRowsProps = Omit<
  TxnActionsMenuProps,
  'anchorEl' | 'setAnchorEl' | 'className' | 'iconClassName' | 'variant' | 'size' | 'width'
> & {
  closeMenu: () => void;
};

const DEFAULT_OPTION_ORDER: TxnMenuOptionEnum[] = [
  TxnMenuOptionEnum.EDIT,
  TxnMenuOptionEnum.INVOICE,
  TxnMenuOptionEnum.LINK_TO_TRANSACTION,
  TxnMenuOptionEnum.RULE,
  TxnMenuOptionEnum.PAYMENT,
  TxnMenuOptionEnum.BILL_PRESENTMENT,
  TxnMenuOptionEnum.EXCLUDE_REPORTS,
  TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN,
  TxnMenuOptionEnum.RECURRING,
  TxnMenuOptionEnum.GOAL,
  TxnMenuOptionEnum.VIEW_SERIES,
  TxnMenuOptionEnum.DELETE,
];

// This component is a menu that displays available actions for a transaction based on the transaction type
// and other attributes. It is generally used in a 3-dot menu button for a given transaction.
//
// Use TxnActionsMenuButton to render the button that opens this menu.
//
function useTxnActionsMenuRows(props: TxnActionsMenuRowsProps): React.ReactNode[] {
  const {
    closeMenu,
    context = 'txns-actions-menu',
    txn,
    splitItem,
    txnActionCallback,
    showOptionsOrder,
    isInvoiceDash,
    splitId,
    rootModalDrawer,
  } = props;

  const txnActionProps = useTxnActionProps()(txn, { isInvoiceDash, splitId });

  const optionsIncludesExclusionItems = useCallback((optionOrder: TxnMenuOptionEnum[]) => {
    return (
      optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_REPORTS) ||
      optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN)
    );
  }, []);

  let options: TxnMenuOption[] = [];

  const optionOrder = showOptionsOrder ?? DEFAULT_OPTION_ORDER;

  const excludeOptions = optionHooks.useExcludeOptions({
    closeMenu,
    context,
    txnActionCallback,
    txnActionProps,
    splitItem,
    hideExcludeReports: !optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_REPORTS),
    hideExcludeSpendingPlan: !optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN),
  });

  let exclusionsInList = false;
  optionOrder.forEach((optionType) => {
    switch (optionType) {
      case TxnMenuOptionEnum.EDIT:
        options = options.concat(
          optionHooks.useEditOptions({ closeMenu, txnActionCallback, context, rootModalDrawer, txnActionProps }),
        );
        break;
      case TxnMenuOptionEnum.LINK_TO_TRANSACTION:
        options = options.concat(
          optionHooks.useLinkToTransactionOptions(
            { closeMenu, context, rootModalDrawer, txnActionProps },
            () => !!txnActionProps.attributes.isMatchable,
          ),
        );
        break;
      case TxnMenuOptionEnum.RULE:
        options = options.concat(optionHooks.useRuleOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.INVOICE:
        options = options.concat(
          optionHooks.useInvoiceOptions(
            { closeMenu, context, txnActionProps },
            () => txnActionProps.txnToEdit.type === 'INVOICE',
          ),
        );
        break;
      case TxnMenuOptionEnum.PAYMENT:
        options = options.concat(optionHooks.usePaymentOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.BILL_PRESENTMENT:
        // options = options.concat(optionHooks.useBillPresentmentOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.EXCLUDE_REPORTS:
        if (!exclusionsInList && optionsIncludesExclusionItems(optionOrder)) {
          options = options.concat(excludeOptions);
        }
        exclusionsInList = true;
        break;
      case TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN:
        if (!exclusionsInList && optionsIncludesExclusionItems(optionOrder)) {
          options = options.concat(excludeOptions);
        }
        exclusionsInList = true;
        break;
      case TxnMenuOptionEnum.RECURRING:
        options = options.concat(
          optionHooks.useRecurringOptions({ closeMenu, context, txnActionCallback, txnActionProps }),
        );
        break;
      case TxnMenuOptionEnum.GOAL:
        options = options.concat(optionHooks.useGoalOptions({ closeMenu, context, txnActionCallback, txnActionProps }));
        break;
      case TxnMenuOptionEnum.VIEW_SERIES:
        options = options.concat(optionHooks.useViewSeriesOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.DELETE:
        options = options.concat(optionHooks.useDeleteOptions({ closeMenu, context, txnActionProps }));
        break;
      default:
        break;
    }
  });

  if (options.length > 0) {
    options[options.length - 1].divider = false;
  }

  return renderTxnMenuOptions(options);
}

// Mount option hooks only while the menu is open. Register and Sales & Billing render one
// trigger per row; running this graph on every closed row re-subscribes to Redux.
function TxnActionsMenuRows({
  autoFocus,
  ...props
}: TxnActionsMenuRowsProps & { autoFocus?: boolean }): React.ReactElement {
  const rows = useTxnActionsMenuRows(props);
  if (!autoFocus) {
    return <>{rows}</>;
  }
  const [first, ...rest] = React.Children.toArray(rows);
  return (
    <>
      {React.isValidElement(first)
        ? React.cloneElement(first as React.ReactElement<{ autoFocus?: boolean }>, { autoFocus: true })
        : first}
      {rest}
    </>
  );
}

export const TxnActionsMenu = (props: TxnActionsMenuProps): React.ReactElement => {
  const { anchorEl, onMenuClose, setAnchorEl, width = 280, ...itemsProps } = props;
  const open = Boolean(anchorEl);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onMenuClose?.();
  }, [onMenuClose, setAnchorEl]);

  return (
    <QMenu id='txn-actions-menu' anchorEl={anchorEl} open={open} onClose={handleClose} width={width}>
      {open ? <TxnActionsMenuRows {...itemsProps} closeMenu={closeMenu} /> : null}
    </QMenu>
  );
};

// This component renders a 3-dot menu button that open the TxnActionsMenu.
//
export const TxnActionsMenuButton = (
  props: Omit<TxnActionsMenuProps, 'anchorEl' | 'setAnchorEl'>,
): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const {
    variant = 'tonal',
    size = 'small',
    width = 280,
    className,
    iconClassName,
    onMenuClose,
    ...itemsProps
  } = props;

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div
      role='none'
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <QDropdownMenu
        id='txn-actions'
        aria-label='actions'
        iconButtonVariant={variant === 'empty' ? 'plain' : variant}
        iconButtonSize={size}
        iconButtonShape='rounded'
        icon={iconClassName ? <MoreVertRoundedIcon className={iconClassName} /> : undefined}
        width={width}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            onMenuClose?.();
          }
        }}
        IconButtonProps={{
          className,
          tabIndex: -1,
        }}
      >
        {open ? <TxnActionsMenuRows {...itemsProps} closeMenu={closeMenu} /> : null}
      </QDropdownMenu>
    </div>
  );
};
